import { v4 as uuidv4 } from 'uuid';
import { SpeakerFaceCoordinatesResponse } from '@goldcast/api/content';
import { AlignedRecordings } from '../../types/clip-types';
import { VideoElement } from '../../types/RemotionPlayerConfigTypes/Element/VideoElement';
import { SceneChanges } from '../../types/scene-changes';
import { getVideoCropPositionMap } from '../../utils/sceneUtils';
import { MINIMUM_SCENE_TIME_DIFFERENCE } from '../../constants/scene.constants';
import { Scene, VideoCropPosition } from '../../types/RemotionPlayerConfigTypes/Scene';
import { SCREENSHARE_VIDEO_ID } from '../../constants/screenshare';
import { ScreenshareBound, SpeakersAnalysis } from '../../types/speakers-analysis';
import { isApproxWindowSubset } from '../../utils/compareUtils';

export function createScenesFromSceneOverrides({
  sceneOverrides,
  fps,
  clipStartFrame,
  clipEndFrame,
  videos,
  facialRecognitionToDefaultPosition,
  recordings
}: {
  sceneOverrides: SceneChanges;
  fps: number;
  clipStartFrame: number;
  clipEndFrame: number;
  videos: VideoElement[];
  facialRecognitionToDefaultPosition?: SpeakerFaceCoordinatesResponse;
  recordings: AlignedRecordings[];
}) {
  const scenes: Scene[] = sceneOverrides
    .map((scene, index) => {
      const sceneStart = scene.offset * fps;
      if (sceneStart > clipEndFrame) {
        return null;
      }

      const isLastScene = index === sceneOverrides.length - 1;
      const sceneEnd = isLastScene ? clipEndFrame : sceneOverrides[index + 1]?.offset * fps;
      if (sceneEnd < clipStartFrame) {
        return null;
      }

      const sceneStartFrame = Math.max(clipStartFrame, sceneStart);
      const sceneEndFrame = Math.min(clipEndFrame, sceneEnd);

      if (sceneStartFrame >= sceneEndFrame) {
        return null;
      }

      const videoCropPositionMap: Scene['details']['video_crop_position_map'] =
        facialRecognitionToDefaultPosition && getVideoCropPositionMap(facialRecognitionToDefaultPosition, recordings);

      return {
        id: uuidv4(),
        name: `Scene ${index}`,
        fromFrame: sceneStartFrame,
        toFrame: sceneEndFrame,
        details: {
          layout: scene.theme_obj.layout,
          fitMode: scene.theme_obj.fit,
          visible_video_ids: videos.map(video => video.id),
          all_video_ids: videos.map(video => video.id),
          video_crop_position_map: videoCropPositionMap
        }
      };
    })
    .filter(scene => scene !== null);

  // there should not be any scene less than MINIMUM_SCENE_TIME_DIFFERENCE * fps
  // If there is a any, it needs to be merged with the next scene
  const minSceneDuration = MINIMUM_SCENE_TIME_DIFFERENCE * fps;
  const reversedScenes = structuredClone(scenes).reverse();
  const validScenes: typeof scenes = [];

  reversedScenes.forEach((currentScene, i) => {
    const sceneDuration = currentScene.toFrame - currentScene.fromFrame;

    if (sceneDuration < minSceneDuration && i < reversedScenes.length - 1) {
      // Get the previous scene (which is next in the reversed array)
      const previousScene = reversedScenes[i + 1];

      // Update previous scene's properties
      previousScene.toFrame = currentScene.toFrame;
      previousScene.details = {
        ...previousScene.details,
        ...currentScene.details
      };
    } else {
      // last scene will definitely be pushed but we're filtering scene start and end at the same frame above
      validScenes.unshift(currentScene);
    }
  });

  return validScenes;
}
const getActiveScreenshareBoundsAtFrame = (
  screenshareBounds: ScreenshareBound[],
  frame: number,
  fps: number
): ScreenshareBound | undefined => {
  const currentTime = frame / fps;
  return screenshareBounds.find(bound =>
    isApproxWindowSubset([currentTime, currentTime], [bound.start_time, bound.end_time])
  );
};

export const getScreenshareCropPositionMap = ({
  frame,
  fps,
  speakersAnalysis
}: {
  frame: number;
  fps: number;
  speakersAnalysis: SpeakersAnalysis;
}): Record<string, VideoCropPosition> | undefined => {
  if (!speakersAnalysis.screenshare_bounds || speakersAnalysis.screenshare_bounds.length === 0) {
    return undefined;
  }

  const screenshareBounds = getActiveScreenshareBoundsAtFrame(speakersAnalysis.screenshare_bounds, frame, fps);

  if (!screenshareBounds) {
    return undefined;
  }

  const cropPosition: VideoCropPosition = {
    top_left: {
      x: screenshareBounds.coordinates.x,
      y: screenshareBounds.coordinates.y
    },
    bottom_right: {
      x: screenshareBounds.coordinates.x + screenshareBounds.coordinates.width,
      y: screenshareBounds.coordinates.y + screenshareBounds.coordinates.height
    }
  };

  const screenshareVideoId = SCREENSHARE_VIDEO_ID;

  return {
    [screenshareVideoId]: cropPosition
  };
};
